import { action, makeObservable, observable, runInAction } from "mobx";
import { EmbeddingDeploymentViewModel } from "models/LLMs/EmbeddingDeploymentViewModel";
import { EmbeddingModelViewModel } from "models/LLMs/EmbeddingModelViewModel";
import { LLMDeploymentViewModel } from "models/LLMs/LLMDeploymentViewModel";
import { LLMHostViewModel } from "models/LLMs/LLMHostViewModel";
import {
    DeploymentConfigTestPayload,
    LLMManagementModelViewModel,
} from "models/LLMs/LLMManagementModelViewModel";
import type {
    CreateEmbeddingModelPayload,
    CreateLLMModelPayload,
} from "models/LLMs/LLMManagementModelViewModel";
import { TestResultViewModel } from "models/LLMs/TestResultViewModel";
import LLMManagementService from "services/LLMManagementService";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "./RootStore";
import type { IRootStore } from "./RootStore";

@AcxStore
class LLMManagementStore extends BaseStore {
    private service: LLMManagementService;

    @observable hosts: LLMHostViewModel[] = [];
    @observable llmModels: LLMManagementModelViewModel[] = [];
    @observable llmDeployments: LLMDeploymentViewModel[] = [];
    @observable embeddingModels: EmbeddingModelViewModel[] = [];
    @observable embeddingDeployments: EmbeddingDeploymentViewModel[] = [];
    @observable isLoading = false;

    constructor(private rootStore: IRootStore) {
        super("LLMManagementStore");
        makeObservable(this);
        this.service = new LLMManagementService();
    }

    @action
    async loadAll() {
        this.isLoading = true;
        try {
            const [
                hosts,
                llmModels,
                llmDeployments,
                embeddingModels,
                embeddingDeployments,
            ] = await Promise.all([
                this.service.getHosts(),
                this.service.getLLMModels(),
                this.service.getLLMDeployments(),
                this.service.getEmbeddingModels(),
                this.service.getEmbeddingDeployments(),
            ]);
            runInAction(() => {
                this.hosts = hosts;
                this.llmModels = llmModels;
                this.llmDeployments = llmDeployments;
                this.embeddingModels = embeddingModels;
                this.embeddingDeployments = embeddingDeployments;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    // Host actions
    @action
    async createHost(host: Omit<LLMHostViewModel, "id" | "isActive">) {
        const created = await this.service.createHost(host);
        runInAction(() => {
            this.hosts.push(created);
        });
        return created;
    }

    @action
    async updateHost(
        id: string,
        host: Omit<LLMHostViewModel, "id" | "isActive">,
    ) {
        const updated = await this.service.updateHost(id, host);
        runInAction(() => {
            this.hosts = this.hosts.map((h) => (h.id === id ? updated : h));
        });
        return updated;
    }

    @action
    async deleteHost(id: string) {
        await this.service.deleteHost(id);
        runInAction(() => {
            this.hosts = this.hosts.filter((h) => h.id !== id);
        });
    }

    async testHost(id: string): Promise<TestResultViewModel> {
        return this.service.testHost(id);
    }

    // LLM Model actions
    @action
    async createLLMModel(model: CreateLLMModelPayload) {
        const created = await this.service.createLLMModel(model);
        runInAction(() => {
            this.llmModels.push(created);
        });
        await this.refreshLLMDeployments();
        return created;
    }

    @action
    private async refreshLLMDeployments() {
        const deployments = await this.service.getLLMDeployments();
        runInAction(() => {
            this.llmDeployments = deployments;
        });
    }

    @action
    private async refreshEmbeddingDeployments() {
        const deployments = await this.service.getEmbeddingDeployments();
        runInAction(() => {
            this.embeddingDeployments = deployments;
        });
    }

    @action
    async updateLLMModel(
        id: string,
        model: Omit<LLMManagementModelViewModel, "id" | "isActive">,
    ) {
        const updated = await this.service.updateLLMModel(id, model);
        runInAction(() => {
            this.llmModels = this.llmModels.map((m) =>
                m.id === id ? updated : m,
            );
        });
        return updated;
    }

    @action
    async deleteLLMModel(id: string) {
        await this.service.deleteLLMModel(id);
        runInAction(() => {
            this.llmModels = this.llmModels.filter((m) => m.id !== id);
        });
    }

    // LLM Deployment actions
    @action
    async createLLMDeployment(
        deployment: Omit<
            LLMDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ) {
        const created = await this.service.createLLMDeployment(deployment);
        runInAction(() => {
            this.llmDeployments.push(created);
        });
        return created;
    }

    @action
    async updateLLMDeployment(
        id: string,
        deployment: Omit<
            LLMDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ) {
        const updated = await this.service.updateLLMDeployment(id, deployment);
        runInAction(() => {
            this.llmDeployments = this.llmDeployments.map((d) =>
                d.id === id ? updated : d,
            );
        });
        return updated;
    }

    @action
    async deleteLLMDeployment(id: string) {
        await this.service.deleteLLMDeployment(id);
        runInAction(() => {
            this.llmDeployments = this.llmDeployments.filter(
                (d) => d.id !== id,
            );
        });
    }

    async testLLMDeployment(id: string): Promise<TestResultViewModel> {
        return this.service.testLLMDeployment(id);
    }

    async testLLMDeploymentConfig(
        payload: DeploymentConfigTestPayload,
    ): Promise<TestResultViewModel> {
        return this.service.testLLMDeploymentConfig(payload);
    }

    // Embedding Model actions
    @action
    async createEmbeddingModel(model: CreateEmbeddingModelPayload) {
        const created = await this.service.createEmbeddingModel(model);
        runInAction(() => {
            this.embeddingModels.push(created);
        });
        await this.refreshEmbeddingDeployments();
        return created;
    }

    @action
    async updateEmbeddingModel(
        id: string,
        model: Omit<EmbeddingModelViewModel, "id" | "isActive">,
    ) {
        const updated = await this.service.updateEmbeddingModel(id, model);
        runInAction(() => {
            this.embeddingModels = this.embeddingModels.map((m) =>
                m.id === id ? updated : m,
            );
        });
        return updated;
    }

    @action
    async deleteEmbeddingModel(id: string) {
        await this.service.deleteEmbeddingModel(id);
        runInAction(() => {
            this.embeddingModels = this.embeddingModels.filter(
                (m) => m.id !== id,
            );
        });
    }

    // Embedding Deployment actions
    @action
    async createEmbeddingDeployment(
        deployment: Omit<
            EmbeddingDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ) {
        const created =
            await this.service.createEmbeddingDeployment(deployment);
        runInAction(() => {
            this.embeddingDeployments.push(created);
        });
        return created;
    }

    @action
    async updateEmbeddingDeployment(
        id: string,
        deployment: Omit<
            EmbeddingDeploymentViewModel,
            "id" | "isActive" | "modelName" | "hostName"
        >,
    ) {
        const updated = await this.service.updateEmbeddingDeployment(
            id,
            deployment,
        );
        runInAction(() => {
            this.embeddingDeployments = this.embeddingDeployments.map((d) =>
                d.id === id ? updated : d,
            );
        });
        return updated;
    }

    @action
    async deleteEmbeddingDeployment(id: string) {
        await this.service.deleteEmbeddingDeployment(id);
        runInAction(() => {
            this.embeddingDeployments = this.embeddingDeployments.filter(
                (d) => d.id !== id,
            );
        });
    }

    async testEmbeddingDeployment(id: string): Promise<TestResultViewModel> {
        return this.service.testEmbeddingDeployment(id);
    }

    async testEmbeddingDeploymentConfig(
        payload: DeploymentConfigTestPayload,
    ): Promise<TestResultViewModel> {
        return this.service.testEmbeddingDeploymentConfig(payload);
    }
}

export default LLMManagementStore;
